import { useState, useEffect } from 'react';
import { Badge, List, Dialog, DotLoading, Switch, Toast } from 'antd-mobile';
import {
  RightOutline,
  PhoneFill,
  MessageOutline,
  BellOutline,
  SetOutline,
  EnvironmentOutline,
  FileOutline,
} from 'antd-mobile-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import PageHeader from '../components/PageHeader';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ReminderHeaderBell from '../components/ReminderHeaderBell';
import { supabase } from '@ubus/shared';
import type { Driver } from '@ubus/shared';
import GPSManager from '../utils/GPSManager';
import { useDriverReminderUnread } from '../hooks/useDriverReminderUnread';

const SUPPORT_PHONE = '+6244220000';

function isGpsEnabled(): boolean {
  return localStorage.getItem('ubus_gps_enabled') !== 'false';
}

function Profile() {
  const { t } = useTranslation();
  const { driverId, user, logout, fleetOperatorName } = useAuth();
  const navigate = useNavigate();
  const [driver, setDriver] = useState<Driver | null>(null);
  const [loading, setLoading] = useState(true);
  const [gpsEnabled, setGpsEnabled] = useState(isGpsEnabled());
  const reminderUnread = useDriverReminderUnread();

  useEffect(() => {
    if (driverId) fetchDriverProfile();
  }, [driverId]);

  const fetchDriverProfile = async () => {
    try {
      const { data, error } = await supabase.from('drivers').select('*').eq('id', driverId).single();
      if (error) throw error;
      setDriver(data);
    } catch (err) {
      console.error('Failed to fetch driver profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGpsChange = async (checked: boolean) => {
    setGpsEnabled(checked);
    localStorage.setItem('ubus_gps_enabled', String(checked));
    if (!checked) {
      GPSManager.stopShiftReporting();
      Toast.show({ content: t('profile.gpsOff') });
      return;
    }
    if (driverId) {
      let tripGpsActive = false;
      try {
        const raw = localStorage.getItem('ubus-gps-active');
        if (raw) {
          const parsed = JSON.parse(raw) as { mode?: string };
          tripGpsActive = parsed.mode === 'trip';
        }
      } catch {
        // ignore
      }
      if (!tripGpsActive) {
        const started = await GPSManager.startShiftReporting(driverId);
        Toast.show({
          content: started ? t('profile.gpsOn') : t('profile.gpsWaitingPermission'),
        });
        return;
      }
    }
    Toast.show({ content: t('profile.gpsOn') });
  };

  const handleContactSupport = () => {
    Dialog.show({
      title: t('profile.contactSupportTitle'),
      content: SUPPORT_PHONE,
      closeOnAction: true,
      actions: [
        [
          { key: 'cancel', text: t('common.cancel') },
          {
            key: 'call',
            text: t('common.call'),
            bold: true,
            onClick: () => {
              window.location.href = `tel:${SUPPORT_PHONE}`;
            },
          },
        ],
      ],
    });
  };

  const handleLogout = () => {
    Dialog.show({
      title: t('profile.logoutTitle'),
      closeOnAction: true,
      actions: [
        [
          { key: 'cancel', text: t('common.cancel') },
          {
            key: 'exit',
            text: t('common.exit'),
            bold: true,
            onClick: async () => {
              await logout();
              navigate('/login', { replace: true });
            },
          },
        ],
      ],
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <DotLoading color="primary" />
      </div>
    );
  }

  const displayEmail = user?.email?.includes('@ubus.local')
    ? driver?.phone || user.email
    : user?.email || driver?.phone || '-';

  return (
    <div className="driver-profile-page">
      <PageHeader
        title={t('profile.title')}
        subtitle={t('profile.subtitle')}
        right={
          <div className="driver-page-header__actions">
            <ReminderHeaderBell variant="onLight" />
            <LanguageSwitcher className="driver-lang-switcher driver-lang-switcher--inline" />
          </div>
        }
      />
      <div className="driver-profile-header">
        <div className="driver-avatar">{driver?.name?.charAt(0)?.toUpperCase() || 'D'}</div>
        <div className="driver-profile-name">{driver?.name || t('profile.driverFallback')}</div>
        {fleetOperatorName ? (
          <div className="driver-profile-phone" style={{ marginTop: 4 }}>
            {fleetOperatorName}
          </div>
        ) : null}
        <div className="driver-profile-phone">{displayEmail}</div>
      </div>

      <div className="driver-card driver-menu-card">
        <List>
          <List.Item
            prefix={<EnvironmentOutline color="var(--primary)" />}
            description={t('profile.gpsDescription')}
            extra={
              <Switch
                checked={gpsEnabled}
                onChange={(checked) => {
                  void handleGpsChange(checked);
                }}
                style={{
                  '--checked-color': '#22C55E',
                  '--height': '22px',
                  '--width': '40px',
                }}
              />
            }
            arrow={false}
          >
            {t('profile.gpsReporting')}
          </List.Item>
          <List.Item
            prefix={<PhoneFill color="var(--primary)" />}
            onClick={handleContactSupport}
            arrow={<RightOutline />}
          >
            {t('profile.contactSupport')}
          </List.Item>
          <List.Item
            prefix={<BellOutline color={reminderUnread > 0 ? '#dc2626' : 'var(--primary)'} />}
            onClick={() => navigate('/reminders')}
            arrow={<RightOutline />}
            extra={
              reminderUnread > 0 ? (
                <Badge content={reminderUnread > 99 ? '99+' : reminderUnread} color="#dc2626" />
              ) : null
            }
          >
            {t('profile.reminders')}
          </List.Item>
          <List.Item
            prefix={<MessageOutline color="var(--primary)" />}
            onClick={() => navigate('/sos-history')}
            arrow={<RightOutline />}
          >
            {t('profile.helpRequests')}
          </List.Item>
          <List.Item
            prefix={<FileOutline color="var(--primary)" />}
            onClick={() => navigate('/inspection?mode=after')}
            arrow={<RightOutline />}
          >
            {t('profile.finishCheck')}
          </List.Item>
          <List.Item
            prefix={<FileOutline color="var(--primary)" />}
            onClick={() => navigate('/inspection-history')}
            arrow={<RightOutline />}
          >
            {t('profile.inspectionHistory')}
          </List.Item>
          <List.Item
            prefix={<SetOutline color="var(--primary)" />}
            onClick={() => navigate('/change-password')}
            arrow={<RightOutline />}
          >
            {t('profile.changePassword')}
          </List.Item>
          <List.Item
            prefix={<SetOutline color="var(--error)" />}
            onClick={handleLogout}
            arrow={false}
          >
            {t('profile.logout')}
          </List.Item>
        </List>
      </div>
    </div>
  );
}

export default Profile;
